<template>
  <div style="height:100%">
    <template v-if="loading">
      <production-loading />
    </template>
    <template v-else-if="error">
      <production-error />
    </template>
    <template v-else-if="!error && !production">
      <production-no-records />
    </template>
    <template v-else>
      <div
        :key="i"
        :class="i !== 0 ? 'mt-8 mb-2' : 'mb-2'"
        class="headline ml-4 font-weight-medium"
        v-for="(shiftData, shiftKey, i) in production"
      >
        <span class="primary--text">
          {{ shiftKey }}
        </span>
        <div
          :key="j"
          class="mt-2"
          v-for="(machineData, machineKey, j) in shiftData"
        >
          <template v-if="isClosureAsset">
            <div class="title d-flex align-center flex-wrap">
              <span class="mr-2">
                {{ machineData.displayName || machineKey }}
              </span>
              <assign-operator
                :shift="shiftKey"
                :machine="machineData.machinename || machineKey"
                :operator="{
                  operatorcode: machineData.operatorcode,
                  operatorname: machineData.operatorname,
                }"
              />
              <div class="d-flex flex-wrap ml-2">
                <div
                  v-for="stage in getStageSummaries(machineData.production)"
                  :key="stage.stageLabel"
                  class="stage-summary-chip mr-1 mb-1 px-2 py-0"
                >
                  <span class="caption font-weight-medium primary--text mr-1">
                    {{ stage.stageLabel }}
                  </span>
                  <span class="info--text">
                    {{ stage.produced }}
                  </span>
                </div>
              </div>
            </div>
            <v-data-table
              class="mt-2"
              :items="[getLineSummary(machineData, shiftKey)]"
              :headers="productionHeaders"
              disable-pagination
              hide-default-footer
            >
              <!-- eslint-disable-next-line -->
              <template #item.firstcycle="{ item }">
                {{ item.firstcycle ? new Date(item.firstcycle).toLocaleTimeString('en-GB') : '-' }}
              </template>
              <!-- eslint-disable-next-line -->
              <template #item.lastcycle="{ item }">
                {{ item.lastcycle ? new Date(item.lastcycle).toLocaleTimeString('en-GB') : '-' }}
              </template>
              <!-- eslint-disable-next-line -->
              <template #item.produced="{ item }">
                <span class="info--text">
                  {{ item.produced }}
                </span>
              </template>
              <!-- eslint-disable-next-line -->
              <template #item.accepted="{ item }">
                <span class="success--text">
                  {{ item.accepted }}
                </span>
              </template>
              <!-- eslint-disable-next-line -->
              <template #item.rejected="{ item }">
                <div class="error--text d-inline-block mt-1">
                  {{ item.rejected }}
                </div>
                <span class="float-right">
                  <assign-rejections
                    :production="item"
                    :rejectiontypeIsHourly="rejectiontypeIsHourly"
                  />
                </span>
              </template>
              <!-- eslint-disable-next-line -->
              <template #item.rework="{ item }">
                <div class="warning--text d-inline-block mt-1">
                  {{ item.rework }}
                </div>
                <span class="float-right">
                  <assign-rework :production="item" />
                </span>
              </template>
              <!-- eslint-disable-next-line -->
              <template #item.color="{ item }">
                <span>{{ item.color || '-' }}</span>
              </template>
              <!-- eslint-disable-next-line -->
              <template #item.scrap="{ item }">
                <div class="warning--text d-inline-block mt-1">
                  {{ parseFloat(item.scrap).toFixed(2) }}
                </div>
                <span class="float-right">
                  <assign-scrap :production="item" />
                </span>
              </template>
            </v-data-table>
          </template>
          <template v-else>
            <div class="title d-inline-block">
              {{ machineData.displayName || machineKey }}
              <assign-operator
                :shift="shiftKey"
                :machine="machineData.machinename || machineKey"
                :operator="{
                  operatorcode: machineData.operatorcode,
                  operatorname: machineData.operatorname,
                }"
              />
            </div>
            <v-data-table
              :items="machineData.production"
              :headers="productionHeaders"
              disable-pagination
              hide-default-footer
            >
              <!-- eslint-disable-next-line -->
              <template #item.firstcycle="{ item }">
                {{ new Date(item.firstcycle).toLocaleTimeString('en-GB') }}
              </template>
              <!-- eslint-disable-next-line -->
              <template #item.lastcycle="{ item }">
                {{ new Date(item.lastcycle).toLocaleTimeString('en-GB') }}
              </template>
              <!-- eslint-disable-next-line -->
              <template #item.stageLabel="{ item }">
                <span>{{ item.stageLabel || item.stage || item.positionname || '-' }}</span>
              </template>
              <!-- eslint-disable-next-line -->
              <template #item.produced="{ item }">
                <span class="info--text">
                  {{ item.produced }}
                </span>
              </template>
              <!-- eslint-disable-next-line -->
              <template #item.accepted="{ item }">
                <span class="success--text">
                  {{ item.accepted }}
                </span>
              </template>
              <!-- eslint-disable-next-line -->
              <template #item.rejected="{ item }">
                <div class="error--text d-inline-block mt-1">
                  {{ item.rejected }}
                </div>
                <span class="float-right">
                  <assign-rejections
                    :production="item"
                    :rejectiontypeIsHourly="rejectiontypeIsHourly"
                  />
                </span>
              </template>
              <!-- eslint-disable-next-line -->
              <template #item.rework="{ item }">
                <div class="warning--text d-inline-block mt-1">
                  {{ item.rework }}
                </div>
                <span class="float-right">
                  <assign-rework :production="item" />
                </span>
              </template>
              <!-- eslint-disable-next-line -->
              <template #item.color="{ item }">
                <span>{{ item.color || '-' }}</span>
              </template>
              <!-- eslint-disable-next-line -->
              <template #item.scrap="{ item }">
                <div class="warning--text d-inline-block mt-1">
                  {{ parseFloat(item.scrap).toFixed(2) }}
                </div>
                <span class="float-right">
                  <assign-scrap :production="item" />
                </span>
              </template>
            </v-data-table>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import ProductionLoading from './ProductionLoading.vue';
import ProductionError from './ProductionError.vue';
import ProductionNoRecords from './ProductionNoRecords.vue';
import AssignOperator from './AssignOperator.vue';
import AssignRejections from './AssignRejections.vue';
import AssignRework from './AssignRework.vue';
import AssignScrap from './AssignScrap.vue';

export default {
  name: 'ProductionList',
  components: {
    ProductionLoading,
    ProductionError,
    ProductionNoRecords,
    AssignOperator,
    AssignRejections,
    AssignRework,
    AssignScrap,
  },
  data() {
    return {
      headers: [
        { text: this.$t('production.plan'), value: 'planid' },
        {
          text: this.$t('production.part'),
          value: 'partname',
          width: '25%',
        },
        { text: this.$t('production.start'), value: 'firstcycle' },
        { text: this.$t('production.end'), value: 'lastcycle' },
        { text: this.$t('production.produced'), value: 'produced' },
        { text: this.$t('production.accepted'), value: 'accepted' },
        { text: this.$t('production.rejected'), value: 'rejected' },
        { text: this.$t('production.rework'), value: 'rework' },
        { text: this.$t('production.scrap'), value: 'scrap' },
      ],
    };
  },
  computed: {
    ...mapState('productionLog', [
      'loading',
      'error',
      'assetId',
      'machines',
    ]),
    ...mapState('webApp', ['appSchema', 'activeAppId']),
    ...mapGetters('productionLog', ['production', 'isClosureAsset']),
    productionHeaders() {
      let headers = [];

      const schemaDetails = this.appSchema.filter(
        (schema) => schema.assetId === this.assetId,
      );

      if (
        schemaDetails
        && schemaDetails.length
        && schemaDetails[0].productionHeaders
        && schemaDetails[0].productionHeaders.length
      ) {
        /* eslint-disable max-len */
        // Ensure the "Color Name" column exists in the table headers.
        // If the schema already contains a column with value 'color', do nothing to avoid duplicates.
        // If it is missing, dynamically insert the column right after the 'partname' column.
        // If 'partname' is not found in the headers, add the color column at the end as a fallback.
        /* eslint-enable max-len */

        if (!headers.find((h) => h.value === 'color')) {
          const partIndex = headers.findIndex(
            (h) => h.value === 'partname',
          );

          const colorColumn = {
            text: 'Color Name',
            value: 'color',
            display: true,
          };

          if (partIndex !== -1) {
            headers.splice(partIndex + 1, 0, colorColumn);
          } else {
            headers.push(colorColumn); // fallback safe
          }
        }

        if (!headers.find((h) => h.value === 'color')) {
          const partIndex = headers.findIndex(
            (h) => h.value === 'partname',
          );

          const colorColumn = {
            text: 'Color Name',
            value: 'color',
            display: true,
          };

          if (partIndex !== -1) {
            headers.splice(partIndex + 1, 0, colorColumn);
          } else {
            headers.push(colorColumn); // fallback safe
          }
        }
        headers = schemaDetails[0].productionHeaders.filter((item) => item.display);
      } else {
        headers = [
          { text: 'Plan', value: 'planid', display: true },
          {
            text: 'Part',
            value: 'partname',
            width: '25%',
            display: true,
          },
          { text: 'Color Name', value: 'color', display: true },
          { text: 'Production start', value: 'firstcycle', display: true },
          { text: 'Production end', value: 'lastcycle', display: true },
          { text: 'Produced', value: 'produced', display: true },
          { text: 'Accepted', value: 'accepted', display: true },
          { text: 'Rejected', value: 'rejected', display: true },
          { text: 'Rework', value: 'rework', display: true },
          { text: 'Scrap (In Kg)', value: 'scrap', display: true },
        ];

        const updatedAppSchema = [...this.appSchema];
        const payload = {
          assetId: this.assetId,
          productionHeaders: headers,
        };

        updatedAppSchema.push(payload);

        this.updateAppSchema({
          appSchema: updatedAppSchema,
          saveForAll: true,
        });
      }

      return headers;
    },
    // Rejection type for data show houlry or shiftwise in rejection dialog (houlry/shiftwise)
    rejectiontypeIsHourly() {
      let rejectiontype = false;
      const schemaDetails = this.appSchema.filter(
        (schema) => schema.assetId === this.assetId,
      );

      if (
        schemaDetails
        && schemaDetails.length
        && schemaDetails[0].productionDataConfig
        && schemaDetails[0].productionDataConfig.length
      ) {
        const dataConfig = schemaDetails[0].productionDataConfig.find(
          (item) => item.value === 'rejectiontypeshift',
        );
        if (dataConfig) {
          rejectiontype = dataConfig.display;
        }
      }

      return rejectiontype;
    },
  },
  methods: {
    ...mapActions('productionLog', ['fetchProductionList']),
    ...mapActions('webApp', ['getAppSchema', 'updateAppSchema']),
    getClosureMachineName(machineData) {
      const lineName = machineData.displayName || machineData.linename;
      const rejectionMachine = this.machines.find((mac) => (
        mac.linename === lineName && mac.isrejection === true
      ));
      return rejectionMachine ? rejectionMachine.machinename : (machineData.machinename
        || lineName);
    },
    getLineSummary(machineData, shiftKey) {
      const primaryMachine = this.isClosureAsset
        ? this.getClosureMachineName(machineData)
        : null;
      const allProduction = machineData.production || [];
      const production = allProduction.filter((item) => (
        !primaryMachine || item.machinename === primaryMachine
      ));
      const summaryProduction = production.length ? production : allProduction;
      const summary = summaryProduction.reduce((acc, item) => {
        acc.produced += Number(item.produced || 0);
        acc.accepted += Number(item.accepted || 0);
        acc.rejected += Number(item.rejected || 0);
        acc.rework += Number(item.rework || 0);
        acc.scrap += Number(item.scrap || 0);
        acc.netweight += Number(item.netweight || 0);
        if (!acc.color && item.color) {
          acc.color = item.color;
        }
        if (!acc.partname && item.partname) {
          acc.partname = item.partname;
        }
        if (!acc.planid && item.planid) {
          acc.planid = item.planid;
        }
        if (!acc.componentclass && item.componentclass) {
          acc.componentclass = item.componentclass;
        }
        if (!acc.firstcycle || item.firstcycle < acc.firstcycle) {
          acc.firstcycle = item.firstcycle;
        }
        if (!acc.lastcycle || item.lastcycle > acc.lastcycle) {
          acc.lastcycle = item.lastcycle;
        }
        return acc;
      }, {
        shift: shiftKey,
        machinename: primaryMachine || machineData.machinename,
        linename: machineData.displayName || machineData.linename,
        displayName: machineData.displayName || machineData.linename,
        operatorcode: machineData.operatorcode,
        operatorname: machineData.operatorname,
        produced: 0,
        accepted: 0,
        rejected: 0,
        rework: 0,
        scrap: 0,
        netweight: 0,
        partname: '',
        color: '',
        planid: '',
        componentclass: '',
        firstcycle: null,
        lastcycle: null,
      });
      if (!summaryProduction.length) {
        return {
          ...summary,
          produced: 0,
          accepted: 0,
          rejected: 0,
          rework: 0,
          scrap: 0,
          netweight: 0,
        };
      }
      return summary;
    },
    getStageSummaries(production = []) {
      const summaries = production.reduce((acc, item) => {
        const rawStageLabel = item.stageLabel
          || item.stage
          || item.positionname
          || item.machinename;
        const stageLabel = String(rawStageLabel || '').split('-').pop().toUpperCase();
        const existing = acc.find((entry) => entry.stageLabel === stageLabel);
        const summaryRow = {
          stageLabel,
          machinename: item.machinename,
          linename: item.linename,
          produced: Number(item.produced || 0),
          accepted: Number(item.accepted || 0),
          rejected: Number(item.rejected || 0),
        };
        if (existing) {
          existing.produced += summaryRow.produced;
          existing.accepted += summaryRow.accepted;
          existing.rejected += summaryRow.rejected;
        } else {
          acc.push(summaryRow);
        }
        return acc;
      }, []);
      return summaries.sort((a, b) => {
        const aOrder = Number.isFinite(a.stageOrder) ? a.stageOrder : Number.MAX_SAFE_INTEGER;
        const bOrder = Number.isFinite(b.stageOrder) ? b.stageOrder : Number.MAX_SAFE_INTEGER;
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        return String(a.stageLabel || '').localeCompare(String(b.stageLabel || ''));
      });
    },

  },
  created() {
    this.fetchProductionList();
  },
};
</script>

<style scoped>
.stage-summary-chip {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.65);
  line-height: 1.1;
}
</style>
